import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import nodeTypesIcon from "../../assets/network-graph/node-types.png";
import peopleIcon from "../../assets/network-graph/people.png";
import {
  ControlCardHeader,
  getEndpointId,
  graphLinkKey,
  hexToRgba,
  LabelWithInfo,
  normalizeKey,
} from "../network-graph/graphShared";
import "../network-graph/NetworkGraph.css";

const COMPANY_WITHOUT_STATEMENT_COLOR = "#8fa9e6";
const COMPANY_WITH_STATEMENT_COLOR = "#7c3aed";
const OFFICER_PERSON_COLOR = "#e3d28b";
const OFFICER_OTHER_ENTITY_COLOR = "#8a7400";
const PSC_PERSON_COLOR = "#efbf8c";
const PSC_OTHER_ENTITY_COLOR = "#c56a1d";

const ENTITY_KIND_OPTIONS = [
  { key: "person", label: "Person" },
  { key: "other_entity", label: "Other Entity" },
];
const DEFAULT_SELECTED_ENTITY_KINDS = ["person"];

function normalizeEntityKindKey(value) {
  return String(value || "").trim().toLowerCase();
}

function getOfficerEntityStyle(entityKind) {
  switch (normalizeEntityKindKey(entityKind)) {
    case "other_entity":
      return { fill: OFFICER_OTHER_ENTITY_COLOR, chipBg: "#f5edbf", chipText: "#6f5d00" };
    default:
      return { fill: OFFICER_PERSON_COLOR, chipBg: "#fdf8e8", chipText: "#8f7a27" };
  }
}

function getPscEntityStyle(entityKind) {
  switch (normalizeEntityKindKey(entityKind)) {
    case "other_entity":
      return { fill: PSC_OTHER_ENTITY_COLOR, chipBg: "#fff0e1", chipText: "#8a4513" };
    default:
      return { fill: PSC_PERSON_COLOR, chipBg: "#fff5eb", chipText: "#a15b1a" };
  }
}

function getEntityChipSx(roleFamily, entityKind) {
  const style =
    roleFamily === "PSC" ? getPscEntityStyle(entityKind) : getOfficerEntityStyle(entityKind);
  return {
    bgcolor: style.chipBg,
    color: style.chipText,
  };
}

function getPersonCategoryColor(category) {
  switch (String(category || "").trim()) {
    case "Officer Person":
      return OFFICER_PERSON_COLOR;
    case "Officer Other Entity":
      return OFFICER_OTHER_ENTITY_COLOR;
    case "PSC Person":
      return PSC_PERSON_COLOR;
    case "PSC Other Entity":
      return PSC_OTHER_ENTITY_COLOR;
    default:
      return OFFICER_PERSON_COLOR;
  }
}

function getNodeDisplayColor(node) {
  if (node?.nodeType === "person") {
    return getPersonCategoryColor(node.personCategory);
  }
  return node?.color || COMPANY_WITHOUT_STATEMENT_COLOR;
}

function getCompanyStatementLabel(category) {
  return category === "with_statement" ? "With statement" : "Without statement";
}

function getRoleFamilyLabel(node) {
  return String(node?.personCategory || "").startsWith("PSC") ? "PSC" : "Officer";
}

function getEntityTypeLabel(node) {
  return String(node?.personCategory || "").includes("Other Entity") ? "Other Entity" : "Person";
}

function buildGraphRequestBody(searchRequest, topology) {
  return {
    search: searchRequest,
    includeCompaniesWithStatement: topology.includeCompaniesWithStatement,
    includeCompaniesWithoutStatement: topology.includeCompaniesWithoutStatement,
    includeOfficers: topology.includeOfficers,
    selectedOfficerKinds: topology.selectedOfficerKinds,
    includePsc: topology.includePsc,
    selectedPscKinds: topology.selectedPscKinds,
    minCompaniesPerPerson: Math.max(0, topology.minCompaniesPerPerson || 0),
    minPeoplePerCompany: Math.max(0, topology.minPeoplePerCompany || 0),
  };
}

function formatList(values) {
  return (values || []).filter(Boolean).join(", ");
}

export default function CompanyHouseNetworkGraph({
  apiEnabled,
  postJson,
  searchRequest,
  setSidebarCompanyNames,
}) {
  const graphRef = useRef(null);
  const canvasRef = useRef(null);
  const lastNodeClickRef = useRef({ id: null, timestamp: 0 });
  const [graph, setGraph] = useState({ nodes: [], links: [], meta: null });
  const [graphRevision, setGraphRevision] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [searchedNodeId, setSearchedNodeId] = useState(null);
  const [companySearch, setCompanySearch] = useState("");
  const [searchNotice, setSearchNotice] = useState("");

  const [includeCompaniesWithStatement, setIncludeCompaniesWithStatement] = useState(false);
  const [includeCompaniesWithoutStatement, setIncludeCompaniesWithoutStatement] = useState(true);
  const [includeOfficers, setIncludeOfficers] = useState(true);
  const [selectedOfficerKinds, setSelectedOfficerKinds] = useState(
    DEFAULT_SELECTED_ENTITY_KINDS
  );
  const [includePsc, setIncludePsc] = useState(true);
  const [selectedPscKinds, setSelectedPscKinds] = useState(DEFAULT_SELECTED_ENTITY_KINDS);
  const [minCompaniesPerPerson, setMinCompaniesPerPerson] = useState(1);
  const [minPeoplePerCompany, setMinPeoplePerCompany] = useState(1);
  const [nodeScale, setNodeScale] = useState(1);

  const graphRequestBody = useMemo(
    () =>
      buildGraphRequestBody(searchRequest, {
        includeCompaniesWithStatement,
        includeCompaniesWithoutStatement,
        includeOfficers,
        selectedOfficerKinds,
        includePsc,
        selectedPscKinds,
        minCompaniesPerPerson,
        minPeoplePerCompany,
      }),
    [
      includeCompaniesWithStatement,
      includeCompaniesWithoutStatement,
      includeOfficers,
      selectedOfficerKinds,
      includePsc,
      selectedPscKinds,
      minCompaniesPerPerson,
      minPeoplePerCompany,
      searchRequest,
    ]
  );

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const updateCanvasSize = (nextWidth, nextHeight) => {
      const width = Math.max(0, Math.round(nextWidth || 0));
      const height = Math.max(0, Math.round(nextHeight || 0));
      setCanvasSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height }
      );
    };

    const element = canvasRef.current;
    const initialRect = element.getBoundingClientRect();
    updateCanvasSize(initialRect.width, initialRect.height);

    if (typeof ResizeObserver === "undefined") {
      const handleResize = () => {
        const rect = element.getBoundingClientRect();
        updateCanvasSize(rect.width, rect.height);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateCanvasSize(entry.contentRect.width, entry.contentRect.height);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!apiEnabled || typeof postJson !== "function") return undefined;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      setError("");

      try {
        const data = await postJson("/api/companyhouse/network/graph", graphRequestBody);
        if (!cancelled) {
          lastNodeClickRef.current = { id: null, timestamp: 0 };
          setSelectedNode(null);
          setHoveredNodeId(null);
          setSearchedNodeId(null);
          setGraph({
            nodes: data?.nodes || [],
            links: data?.links || [],
            meta: data?.meta || null,
          });
          setGraphRevision((current) => current + 1);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(`Company House network loading failed: ${requestError.message}`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [apiEnabled, graphRequestBody, postJson]);

  const visibleGraph = graph;
  const renderedGraph = useMemo(
    () => ({
      nodes: (visibleGraph.nodes || []).map((node) => ({ ...node })),
      links: (visibleGraph.links || []).map((link) => ({ ...link })),
    }),
    [visibleGraph]
  );
  const nodeById = useMemo(
    () => new Map((visibleGraph.nodes || []).map((node) => [node.id, node])),
    [visibleGraph.nodes]
  );

  useEffect(() => {
    if (selectedNode && !visibleGraph.nodes.some((node) => node.id === selectedNode.id)) {
      setSelectedNode(null);
    }
  }, [selectedNode, visibleGraph.nodes]);

  useEffect(() => {
    if (hoveredNodeId && !visibleGraph.nodes.some((node) => node.id === hoveredNodeId)) {
      setHoveredNodeId(null);
    }
  }, [hoveredNodeId, visibleGraph.nodes]);

  const interactionNodeId = hoveredNodeId || selectedNode?.id || searchedNodeId || null;

  const hoverContext = useMemo(() => {
    if (!interactionNodeId) return null;

    const highlightedNodeIds = new Set([interactionNodeId]);
    const highlightedLinkKeys = new Set();

    visibleGraph.links.forEach((link) => {
      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      if (sourceId === interactionNodeId || targetId === interactionNodeId) {
        highlightedNodeIds.add(sourceId);
        highlightedNodeIds.add(targetId);
        highlightedLinkKeys.add(graphLinkKey(link));
      }
    });

    return { highlightedNodeIds, highlightedLinkKeys };
  }, [interactionNodeId, visibleGraph.links]);

  const nodePaint = useCallback(
    (node, ctx, globalScale) => {
      const radius = (node.size || 8) * nodeScale;
      const fontSize = Math.max(8, 12 / globalScale);
      const isHighlighted = !hoverContext || hoverContext.highlightedNodeIds.has(node.id);
      const isFocusNode = node.id === interactionNodeId;
      const isSelected = selectedNode?.id === node.id;
      const isHovered = hoveredNodeId === node.id;
      const nodeColor = getNodeDisplayColor(node);

      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
      ctx.fillStyle = isHighlighted ? nodeColor : hexToRgba(nodeColor, 0.16);
      ctx.fill();
      ctx.lineWidth = isSelected ? 2.8 : isHovered ? 2 : 1;
      ctx.strokeStyle = isSelected ? "#0f172a" : isHovered ? "#1f4d88" : "#cbd5e1";
      ctx.stroke();

      if (isFocusNode) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 3, 0, 2 * Math.PI, false);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "rgba(31,77,136,0.75)";
        ctx.stroke();
      }

      const label = node.label || node.id;
      ctx.font = `${fontSize}px "Segoe UI", Tahoma, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = isHighlighted ? "#243042" : "rgba(36, 48, 66, 0.32)";
      ctx.fillText(label, node.x, node.y + radius + 2);
    },
    [hoverContext, hoveredNodeId, interactionNodeId, nodeScale, selectedNode]
  );

  const focusGraphOnNode = useCallback((node, duration = 600) => {
    if (!node || !graphRef.current) return;
    if (typeof node.x === "number" && typeof node.y === "number") {
      graphRef.current.centerAt(node.x, node.y, duration);
      graphRef.current.zoom(2.1, duration);
    }
  }, []);

  const fitGraphToVisibleArea = useCallback((duration = 500) => {
    if (!graphRef.current || !canvasRef.current || !visibleGraph.nodes.length) return;

    const bbox = graphRef.current.getGraphBbox?.();
    if (!bbox?.x || !bbox?.y) {
      graphRef.current.zoomToFit(duration, 60);
      return;
    }

    const graphWidth = Math.max(1, bbox.x[1] - bbox.x[0]);
    const graphHeight = Math.max(1, bbox.y[1] - bbox.y[0]);
    const canvasRect = canvasRef.current.getBoundingClientRect();

    const tabsElement = document.querySelector("[data-right-panel-tabs='true']");
    let coveredRightWidth = 0;
    if (tabsElement) {
      const tabsRect = tabsElement.getBoundingClientRect();
      const verticalOverlap =
        Math.min(canvasRect.bottom, tabsRect.bottom) - Math.max(canvasRect.top, tabsRect.top);
      if (verticalOverlap > 0) {
        coveredRightWidth = Math.max(0, canvasRect.right - tabsRect.left + 16);
      }
    }

    const padding = 60;
    const availableWidth = Math.max(140, canvasRect.width - coveredRightWidth - padding * 2);
    const availableHeight = Math.max(140, canvasRect.height - padding * 2);
    const zoom = Math.min(availableWidth / graphWidth, availableHeight / graphHeight);

    if (!Number.isFinite(zoom) || zoom <= 0) {
      graphRef.current.zoomToFit(duration, 60);
      return;
    }

    const centerX = (bbox.x[0] + bbox.x[1]) / 2 - coveredRightWidth / (2 * zoom);
    const centerY = (bbox.y[0] + bbox.y[1]) / 2;
    graphRef.current.centerAt(centerX, centerY, duration);
    graphRef.current.zoom(zoom, duration);
  }, [visibleGraph.nodes.length]);

  const scheduleFitGraphToVisibleArea = useCallback((duration = 500) => {
    if (typeof window === "undefined") {
      fitGraphToVisibleArea(duration);
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        fitGraphToVisibleArea(duration);
      });
    });
  }, [fitGraphToVisibleArea]);

  const activeSelectedNode = useMemo(() => {
    if (!selectedNode) return null;
    return visibleGraph.nodes.find((node) => node.id === selectedNode.id) || selectedNode;
  }, [selectedNode, visibleGraph.nodes]);

  const handleResetZoom = useCallback(() => {
    if (activeSelectedNode) {
      focusGraphOnNode(activeSelectedNode, 500);
      return;
    }
    scheduleFitGraphToVisibleArea(500);
  }, [activeSelectedNode, focusGraphOnNode, scheduleFitGraphToVisibleArea]);

  const handleClearFocus = useCallback(() => {
    setCompanySearch("");
    setSearchNotice("");
    setSearchedNodeId(null);
    setHoveredNodeId(null);
    setSelectedNode(null);
    if (typeof setSidebarCompanyNames === "function") {
      setSidebarCompanyNames([]);
    }
    scheduleFitGraphToVisibleArea(500);
  }, [scheduleFitGraphToVisibleArea, setSidebarCompanyNames]);

  const handleCompanySearch = useCallback(
    (event) => {
      event.preventDefault();
      const query = companySearch.trim().toLowerCase();
      if (!query) {
        setSearchedNodeId(null);
        setSearchNotice("");
        return;
      }

      const visibleCompanies = visibleGraph.nodes.filter((node) => node.nodeType === "company");
      const found = visibleCompanies.find((node) =>
        String(node.label || "").toLowerCase().includes(query)
      );

      if (!found) {
        setSearchedNodeId(null);
        setSearchNotice("No visible company matches this search.");
        return;
      }

      setSelectedNode(found);
      setHoveredNodeId(null);
      setSearchedNodeId(found.id);
      setCompanySearch(found.label || "");
      setSearchNotice(`Highlighted: ${found.label}`);
    },
    [companySearch, visibleGraph.nodes]
  );

  const getVisibleConnectedCompanyNames = useCallback(
    (nodeId) => {
      const companyNodeById = new Map(
        visibleGraph.nodes
          .filter((node) => node.nodeType === "company")
          .map((node) => [node.id, node])
      );
      const seen = new Set();
      const companyNames = [];

      visibleGraph.links.forEach((link) => {
        const sourceId = getEndpointId(link.source);
        const targetId = getEndpointId(link.target);
        if (sourceId !== nodeId && targetId !== nodeId) return;

        const otherId = sourceId === nodeId ? targetId : sourceId;
        const companyNode = companyNodeById.get(otherId);
        if (!companyNode) return;

        const label = String(companyNode.label || companyNode.id || "").trim();
        const key = normalizeKey(label);
        if (!key || seen.has(key)) return;

        seen.add(key);
        companyNames.push(label);
      });

      return companyNames.sort((left, right) => left.localeCompare(right));
    },
    [visibleGraph.links, visibleGraph.nodes]
  );

  useEffect(() => {
    if (!graphRef.current) return;
    const chargeForce = graphRef.current.d3Force("charge");
    if (chargeForce?.strength) {
      chargeForce.strength((node) => (node?.nodeType === "person" ? -250 : -145));
    }
    const linkForce = graphRef.current.d3Force("link");
    if (linkForce?.distance) {
      linkForce.distance((link) => {
        const sourceNode = nodeById.get(getEndpointId(link.source));
        const targetNode = nodeById.get(getEndpointId(link.target));
        const personNode =
          sourceNode?.nodeType === "person"
            ? sourceNode
            : targetNode?.nodeType === "person"
            ? targetNode
            : null;

        if (personNode?.personCategory === "PSC Other Entity") {
          return 126;
        }
        if (personNode?.personCategory === "PSC Person") {
          return 112;
        }
        if (personNode?.personCategory === "Officer Other Entity") {
          return 108;
        }
        if (personNode?.personCategory === "Officer Person") {
          return 102;
        }
        return 90;
      });
    }
    if (linkForce?.strength) {
      linkForce.strength((link) => {
        const sourceNode = nodeById.get(getEndpointId(link.source));
        const targetNode = nodeById.get(getEndpointId(link.target));
        return sourceNode?.nodeType === "person" || targetNode?.nodeType === "person" ? 0.34 : 0.2;
      });
    }
    if (typeof graphRef.current.d3ReheatSimulation === "function") {
      graphRef.current.d3ReheatSimulation();
    }
  }, [graphRevision, nodeById]);

  useEffect(() => {
    if (!activeSelectedNode || canvasSize.width <= 0 || canvasSize.height <= 0) return undefined;

    let rafId = 0;
    let nestedRafId = 0;
    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        const visibleSelectedNode = visibleGraph.nodes.find((node) => node.id === activeSelectedNode.id);
        focusGraphOnNode(visibleSelectedNode || activeSelectedNode, 450);
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(nestedRafId);
    };
  }, [activeSelectedNode, canvasSize.height, canvasSize.width, focusGraphOnNode, visibleGraph.nodes]);

  useEffect(() => {
    if (
      graphRevision === 0 ||
      activeSelectedNode ||
      canvasSize.width <= 0 ||
      canvasSize.height <= 0 ||
      !visibleGraph.nodes.length
    ) {
      return undefined;
    }

    let rafId = 0;
    let nestedRafId = 0;
    let timeoutId = 0;
    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          fitGraphToVisibleArea(350);
        }, 140);
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(nestedRafId);
      window.clearTimeout(timeoutId);
    };
  }, [activeSelectedNode, canvasSize.height, canvasSize.width, fitGraphToVisibleArea, graphRevision, visibleGraph.nodes.length]);

  const selectedCompanyPeople = useMemo(() => {
    if (!activeSelectedNode || activeSelectedNode.nodeType !== "company") return [];

    const people = [];
    const seen = new Set();
    (visibleGraph.links || []).forEach((link) => {
      if (link.linkType !== "person") return;

      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      const personId =
        sourceId === activeSelectedNode.id
          ? targetId
          : targetId === activeSelectedNode.id
          ? sourceId
          : null;

      if (!personId || seen.has(personId)) return;
      const personNode = nodeById.get(personId);
      if (!personNode) return;

      seen.add(personId);
      people.push(personNode);
    });

    return people;
  }, [activeSelectedNode, nodeById, visibleGraph.links]);

  if (!apiEnabled) {
    return (
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Network View Requires API Mode
        </Typography>
        <Typography sx={{ color: "#475569" }}>
          This graph is server-driven. Start the client with `REACT_APP_DATA_SOURCE=api`
          and the backend endpoint `POST /api/companyhouse/network/graph`.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Paper sx={{ p: 1, borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
        <Box className="network-graph__toolbar-topline">
          <Typography className="network-graph__intro">
            Left workspace filters choose the company slice first. These compact controls only change graph topology.
          </Typography>
        </Box>

        <Box
          className="network-graph__control-grid"
          sx={{ gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}
        >
          <Box className="network-graph__control-section">
            <ControlCardHeader
              icon={peopleIcon}
              title="People"
              info="Add Company House officers and persons of significant control, then choose whether each role family keeps people or other entities."
            />
            <Typography className="network-graph__control-help network-graph__control-help--tight">
              Shared people and other entities connect multiple companies inside the current Company House slice.
            </Typography>
            <Box className="network-graph__control-stack">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeOfficers}
                    onChange={(event) => setIncludeOfficers(event.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Show officers"
                    info="Return officer nodes and their company links."
                  />
                }
              />
              <FormControl size="small" disabled={!includeOfficers}>
                <InputLabel>Officer entity types</InputLabel>
                <Select
                  multiple
                  value={selectedOfficerKinds}
                  onChange={(event) =>
                    setSelectedOfficerKinds(
                      typeof event.target.value === "string"
                        ? event.target.value.split(",")
                        : event.target.value
                    )
                  }
                  input={<OutlinedInput label="Officer entity types" />}
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                          .map((value) =>
                            ENTITY_KIND_OPTIONS.find((option) => option.key === value)?.label || value
                          )
                          .join(", ")
                      : "All"
                  }
                >
                  {ENTITY_KIND_OPTIONS.map((option) => (
                    <MenuItem key={`officer-kind-${option.key}`} value={option.key}>
                      <Checkbox checked={selectedOfficerKinds.includes(option.key)} />
                      <Chip
                        label={option.label}
                        size="small"
                        sx={getEntityChipSx("Officer", option.key)}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includePsc}
                    onChange={(event) => setIncludePsc(event.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Show PSCs"
                    info="Return persons of significant control and other PSC entities, with their company links."
                  />
                }
              />
              <FormControl size="small" disabled={!includePsc}>
                <InputLabel>PSC entity types</InputLabel>
                <Select
                  multiple
                  value={selectedPscKinds}
                  onChange={(event) =>
                    setSelectedPscKinds(
                      typeof event.target.value === "string"
                        ? event.target.value.split(",")
                        : event.target.value
                    )
                  }
                  input={<OutlinedInput label="PSC entity types" />}
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                          .map((value) =>
                            ENTITY_KIND_OPTIONS.find((option) => option.key === value)?.label || value
                          )
                          .join(", ")
                      : "All"
                  }
                >
                  {ENTITY_KIND_OPTIONS.map((option) => (
                    <MenuItem key={`psc-kind-${option.key}`} value={option.key}>
                      <Checkbox checked={selectedPscKinds.includes(option.key)} />
                      <Chip
                        label={option.label}
                        size="small"
                        sx={getEntityChipSx("PSC", option.key)}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className="network-graph__compact-field-grid">
              <TextField
                label="Min co. / person"
                type="number"
                size="small"
                value={minCompaniesPerPerson}
                onChange={(event) =>
                  setMinCompaniesPerPerson(Math.max(0, Number(event.target.value || 0)))
                }
                className="network-graph__mini-field"
                disabled={!includeOfficers && !includePsc}
              />
            </Box>
            <Typography className="network-graph__micro-note">
              Entity colors are shown inside the dropdowns.
            </Typography>
          </Box>

          <Box className="network-graph__control-section">
            <ControlCardHeader
              icon={nodeTypesIcon}
              title="Company"
              info="Company House companies already filtered by the left workspace controls, with statement coverage shown directly on the company nodes."
            />
            <Typography className="network-graph__control-help network-graph__control-help--tight">
              Company nodes come from the same Company House query used by visualisations and company list.
            </Typography>
            <Box className="network-graph__control-stack">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCompaniesWithoutStatement}
                    onChange={(event) => setIncludeCompaniesWithoutStatement(event.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Companies without statement"
                    color={COMPANY_WITHOUT_STATEMENT_COLOR}
                    info="Keep companies that do not carry a Company House PSC statement record."
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCompaniesWithStatement}
                    onChange={(event) => setIncludeCompaniesWithStatement(event.target.checked)}
                  />
                }
                label={
                  <LabelWithInfo
                    label="Companies with statement"
                    color={COMPANY_WITH_STATEMENT_COLOR}
                    info="Keep companies that carry a Company House PSC statement record."
                  />
                }
              />
            </Box>
            <Box className="network-graph__compact-field-grid">
              <TextField
                label="Min people / company"
                type="number"
                size="small"
                value={minPeoplePerCompany}
                onChange={(event) =>
                  setMinPeoplePerCompany(Math.max(0, Number(event.target.value || 0)))
                }
                className="network-graph__mini-field"
                disabled={!includeOfficers && !includePsc}
              />
            </Box>
          </Box>
        </Box>

        <Box className="network-graph__toolbar-footer">
          <Box className="network-graph__focus-inline">
            <Box component="form" onSubmit={handleCompanySearch} className="network-graph__search">
              <TextField
                label="Find in graph"
                size="small"
                value={companySearch}
                onChange={(event) => setCompanySearch(event.target.value)}
              />
              <Button type="submit" variant="contained">Find</Button>
              <Button variant="outlined" onClick={handleClearFocus}>
                Clear
              </Button>
              <Button variant="outlined" onClick={handleResetZoom}>Reset zoom</Button>
            </Box>
            <Typography className="network-graph__interaction-note">
              {searchNotice ||
                "Click explores locally. Double-click a company to filter that company; double-click a person or other entity to filter the visible connected companies."}
            </Typography>
          </Box>
          <Box className="network-graph__read-inline">
            <Typography className="network-graph__read-line network-graph__read-line--inline">
              <strong>How to read:</strong> company size=visible people. Company color=statement coverage. People size=visible connected companies.
            </Typography>
            <Box className="network-graph__node-scale">
              <Typography className="network-graph__node-scale-label">
                Node size {nodeScale.toFixed(1)}x
              </Typography>
              <Slider
                size="small"
                min={0.6}
                max={3}
                step={0.1}
                value={nodeScale}
                onChange={(_, value) => setNodeScale(Array.isArray(value) ? value[0] : value)}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box className={`network-graph__layout${activeSelectedNode ? " network-graph__layout--detail" : ""}`}>
        <Paper ref={canvasRef} className="network-graph__canvas">
          {loading && (
            <Box className="network-graph__overlay">
              <CircularProgress size={22} />
              <Typography>Refreshing network...</Typography>
            </Box>
          )}
          {error ? <Box className="network-graph__error">{error}</Box> : null}
          <ForceGraph2D
            key={graphRevision}
            ref={graphRef}
            width={Math.max(320, canvasSize.width)}
            height={Math.max(520, canvasSize.height)}
            graphData={renderedGraph}
            nodeLabel={(node) => node.label}
            nodeCanvasObject={nodePaint}
            nodePointerAreaPaint={(node, color, ctx) => {
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, (node.size || 8) * nodeScale + 6, 0, 2 * Math.PI, false);
              ctx.fill();
            }}
            linkColor={(link) => {
              const sourceNode = nodeById.get(getEndpointId(link.source));
              const targetNode = nodeById.get(getEndpointId(link.target));
              const personNode =
                sourceNode?.nodeType === "person"
                  ? sourceNode
                  : targetNode?.nodeType === "person"
                  ? targetNode
                  : null;
              const baseColor = personNode ? getNodeDisplayColor(personNode) : "#9eb0c5";
              if (!hoverContext) return hexToRgba(baseColor, 0.82);
              return hoverContext.highlightedLinkKeys.has(graphLinkKey(link))
                ? hexToRgba(baseColor, 0.82)
                : hexToRgba(baseColor, 0.14);
            }}
            linkWidth={(link) =>
              hoverContext?.highlightedLinkKeys.has(graphLinkKey(link)) ? 1.8 : 1.1
            }
            d3VelocityDecay={0.28}
            cooldownTicks={120}
            onNodeClick={(node) => {
              const now = Date.now();
              const isSameNodeDoubleClick =
                lastNodeClickRef.current.id === node.id &&
                now - lastNodeClickRef.current.timestamp < 325;

              lastNodeClickRef.current = { id: node.id, timestamp: now };
              setSelectedNode(node);
              setSearchedNodeId(node.id);

              if (!isSameNodeDoubleClick || typeof setSidebarCompanyNames !== "function") {
                return;
              }

              if (node.nodeType === "company") {
                const companyName = String(node.label || "").trim();
                if (companyName) {
                  setSidebarCompanyNames([companyName]);
                  setSearchNotice(`Filtered workspace to: ${companyName}`);
                }
                return;
              }

              if (node.nodeType === "person") {
                const companyNames = getVisibleConnectedCompanyNames(node.id);
                if (companyNames.length > 0) {
                  setSidebarCompanyNames(companyNames);
                  setSearchNotice(
                    `Filtered workspace to ${companyNames.length} visible companies connected to ${node.label}.`
                  );
                } else {
                  setSearchNotice(`No visible companies are connected to ${node.label}.`);
                }
              }
            }}
            onNodeHover={(node) => setHoveredNodeId(node?.id || null)}
            onBackgroundClick={() => {
              setSelectedNode(null);
              setHoveredNodeId(null);
            }}
            backgroundColor="#f8fbff"
          />
        </Paper>

        <Box className="network-graph__side">
          <Paper className="network-graph__card">
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Visible Metrics</Typography>
            <Typography>Companies: <strong>{visibleGraph.nodes.filter((node) => node.nodeType === "company").length}</strong></Typography>
            <Typography>People: <strong>{visibleGraph.nodes.filter((node) => node.nodeType === "person").length}</strong></Typography>
            <Typography>Links: <strong>{visibleGraph.links.length}</strong></Typography>
            <Typography sx={{ mt: 1 }}>
              Backend graph already includes the current left filters and graph topology filters.
            </Typography>
          </Paper>
        </Box>

        {activeSelectedNode ? (
          <Paper className="network-graph__detail">
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {activeSelectedNode.label}
            </Typography>

            {activeSelectedNode.nodeType === "company" ? (
              <Box className="network-graph__detail-list">
                <Typography><strong>Type:</strong> Company</Typography>
                <Typography><strong>Company number:</strong> {activeSelectedNode.companyNumber || "Unknown"}</Typography>
                <Typography><strong>Status:</strong> {activeSelectedNode.companyStatus || "Unknown"}</Typography>
                <Typography><strong>Company type:</strong> {activeSelectedNode.companyType || "Unknown"}</Typography>
                <Typography><strong>Incorporated on:</strong> {activeSelectedNode.incorporatedOn || "Unknown"}</Typography>
                <Typography><strong>Statement coverage:</strong> {getCompanyStatementLabel(activeSelectedNode.companyStatementCategory)}</Typography>
                <Typography><strong>Visible people:</strong> {activeSelectedNode.peopleCount ?? 0}</Typography>
                <Typography><strong>SIC codes:</strong> {formatList(activeSelectedNode.sicCodes) || "Unknown"}</Typography>
                {selectedCompanyPeople.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Connected people</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selectedCompanyPeople.slice(0, 12).map((person) => (
                        <Button
                          key={person.id}
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedNode(person)}
                          sx={{
                            minHeight: 22,
                            px: 0.8,
                            fontSize: "0.58rem",
                            textTransform: "none",
                            borderColor: hexToRgba(getNodeDisplayColor(person), 0.45),
                            color: "#334155",
                          }}
                        >
                          {person.label} ({getRoleFamilyLabel(person)})
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ) : null}
                {activeSelectedNode.sourceUrl ? (
                  <Typography component="a" href={activeSelectedNode.sourceUrl} target="_blank" rel="noreferrer">
                    Open company
                  </Typography>
                ) : null}
              </Box>
            ) : (
              <Box className="network-graph__detail-list">
                <Typography><strong>Type:</strong> {getEntityTypeLabel(activeSelectedNode)}</Typography>
                <Typography><strong>Category:</strong> {getRoleFamilyLabel(activeSelectedNode)}</Typography>
                <Typography><strong>Visible connected companies:</strong> {activeSelectedNode.companyCount ?? 0}</Typography>
                <Typography><strong>Roles:</strong> {formatList(activeSelectedNode.roleLabels) || "Unknown"}</Typography>
                <Typography><strong>Statuses:</strong> {formatList(activeSelectedNode.statusLabels) || "Unknown"}</Typography>
                <Typography><strong>Nationality:</strong> {activeSelectedNode.nationality || "Unknown"}</Typography>
                <Typography><strong>Country of residence:</strong> {activeSelectedNode.countryOfResidence || "Unknown"}</Typography>
                <Typography><strong>Date of birth:</strong> {activeSelectedNode.dateOfBirth || "Unknown"}</Typography>
                {activeSelectedNode.primaryNatureOfControl ? (
                  <Typography><strong>Nature of control:</strong> {activeSelectedNode.primaryNatureOfControl}</Typography>
                ) : null}
                {activeSelectedNode.primaryAppointmentUrl ? (
                  <Typography component="a" href={activeSelectedNode.primaryAppointmentUrl} target="_blank" rel="noreferrer">
                    Open appointment
                  </Typography>
                ) : null}
              </Box>
            )}
          </Paper>
        ) : null}
      </Box>
    </Box>
  );
}
